import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Wrench, Phone, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getGuide, GUIDES } from '../guidesData';
import { CONTACT } from '../lyphline';

const GuideArticle = () => {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/guides" replace />;
  const related = GUIDES.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-article">
      <SEO title={g.title} description={g.metaDescription} keywords={g.keywords} faq={g.faq} />
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 pt-14 pb-16">
        <Link to="/guides" className="inline-flex items-center gap-1 text-sm text-[#B87333] font-semibold hover:underline"><ArrowLeft className="h-4 w-4" /> All guides</Link>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-5 leading-tight">{g.title}</h1>
        <p className="text-slate-500 mt-3">{g.excerpt}</p>

        <div className="mt-8 space-y-8">
          {g.sections.map((s, i) => (
            <section key={i} data-testid={`guide-section-${i}`}>
              <h2 className="text-xl font-bold text-[#003456] mb-3">{s.h}</h2>
              {s.p.map((para, j) => <p key={j} className="text-slate-700 leading-relaxed mb-3">{para}</p>)}
            </section>
          ))}
        </div>

        {g.faq?.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-[#003456] mb-4">Frequently asked</h2>
            <div className="space-y-4">
              {g.faq.map((f, i) => (
                <div key={i} className="bg-[#F1F5F9] rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-[#003456]">{f.q}</h3>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[#003456] rounded-2xl p-7 text-center">
          <h2 className="text-2xl font-bold text-white">Not sure what's wrong with your machine?</h2>
          <p className="text-white/70 mt-2 text-sm">Get a free diagnostic quote — no obligation.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Link to="/repair/quote" className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3 rounded-md transition-colors"><Wrench className="h-4 w-4" /> Get my free quote</Link>
            <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-7 py-3 rounded-md hover:bg-white/10 transition-colors"><Phone className="h-4 w-4" /> {CONTACT.phone}</a>
          </div>
        </div>

        {/* Related */}
        <div className="mt-12">
          <h2 className="text-lg font-bold text-[#003456] mb-4">Related guides</h2>
          <div className="space-y-2">
            {related.map((r) => (
              <Link key={r.slug} to={`/guides/${r.slug}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-slate-200 hover:border-[#CE8946] transition-colors" data-testid={`related-guide-${r.slug}`}>
                <span className="text-sm text-[#003456] font-medium">{r.title}</span>
                <ArrowRight className="h-4 w-4 text-[#CE8946]" />
              </Link>
            ))}
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default GuideArticle;
